import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const PortalAuthContext = createContext();

// Same rule as AuthContext: a status the server actually returned is a verdict
// worth acting on, a failure to reach the server at all is not. 401 is missing
// or expired, 403 is a token that failed jwt.verify, 404 is a customer row that
// no longer exists. A 5xx leaves the session alone so a backend blip does not
// sign every portal customer out.
const PORTAL_CACHED_CUSTOMER_KEY = 'cachedPortalCustomer';

const isRealAuthFailure = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403 || status === 404;
};

const readCachedCustomer = () => {
  try {
    const raw = localStorage.getItem(PORTAL_CACHED_CUSTOMER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeCachedCustomer = (customerData) => {
  try {
    localStorage.setItem(PORTAL_CACHED_CUSTOMER_KEY, JSON.stringify(customerData));
  } catch (e) {
    /* Storage unavailable: portal simply will not work offline. */
  }
};

const clearCachedCustomer = () => {
  try {
    localStorage.removeItem(PORTAL_CACHED_CUSTOMER_KEY);
  } catch (e) {
    /* Nothing useful to do. */
  }
};

export const usePortalAuth = () => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  }
  return context;
};

export const PortalAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('portalToken');
    if (token) {
      loadCustomer(token);
    } else {
      setLoading(false);
    }
  }, []);

  const loadCustomer = async (token) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/portal/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data);
      writeCachedCustomer(response.data);
    } catch (err) {
      if (isRealAuthFailure(err)) {
        console.error('Portal auth rejected, signing out:', err.response.status);
        localStorage.removeItem('portalToken');
        clearCachedCustomer();
        setCustomer(null);
      } else {
        // Offline or server unreachable - keep the token and the last known
        // profile rather than presenting this as a signed-out portal.
        console.warn('Could not reach /api/portal/me; keeping cached session.', err.message);
        const cached = readCachedCustomer();
        if (cached) setCustomer(cached);
      }
    } finally {
      setLoading(false);
    }
  };

  // Login with email and password
  // companyId is sent on the second step, when the email and password open an
  // account at more than one company and the customer has picked one.
  const login = async (email, password, companyId) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/portal/login`, {
        email,
        password,
        ...(companyId ? { companyId } : {}),
      });
      if (response.data.chooseCompany) {
        return { success: false, chooseCompany: true, companies: response.data.companies || [] };
      }
      const { token, customer: customerData } = response.data;

      localStorage.setItem('portalToken', token);
      setCustomer(customerData);
      writeCachedCustomer(customerData);

      return { success: true, customer: customerData };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Register with invite token and password
  const register = async (token, password) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/portal/register`, {
        token,
        password
      });
      const { token: jwtToken, customer: customerData } = response.data;

      localStorage.setItem('portalToken', jwtToken);
      setCustomer(customerData);
      writeCachedCustomer(customerData);

      return { success: true, customer: customerData };
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Request password reset
  const forgotPassword = async (email) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/portal/forgot-password`, {
        email
      });
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to process request';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Reset password with token
  const resetPassword = async (token, password) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/portal/reset-password`, {
        token,
        password
      });
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to reset password';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('portalToken');
    clearCachedCustomer();
    setCustomer(null);
  };

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('portalToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const refreshCustomer = useCallback(() => {
    const token = localStorage.getItem('portalToken');
    if (token) loadCustomer(token);
  }, []);

  const value = useMemo(() => ({
    customer,
    loading,
    error,
    login,
    register,
    forgotPassword,
    resetPassword,
    logout,
    getAuthHeader,
    isAuthenticated: !!customer,
    refreshCustomer
  }), [customer, loading, error, getAuthHeader, refreshCustomer]);

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
};
